import React from "react";
import { useNavigate } from "react-router-dom";
import { HiTrophy, HiChevronRight } from "react-icons/hi2";
import { useUserEvents } from "../../hooks/useUserEvents";
import { isEventUpcoming } from "../../types/event";
import { daysUntil, formatDateRange, todayYYYYMMDD } from "../../utils/goalDates";

/**
 * Countdown to the next event (Events.md §5), shown near the daily targets:
 * the soonest upcoming, non-withdrawn event; hidden when there is none.
 */
const NextEventChip: React.FC<{ userId: string | undefined }> = ({
  userId,
}) => {
  const navigate = useNavigate();
  const { events } = useUserEvents(userId);
  const today = todayYYYYMMDD();
  const upcoming = events
    .filter((e) => isEventUpcoming(e, today))
    .sort((a, b) => a.date.localeCompare(b.date));
  const next = upcoming[0];
  if (!next) return null;
  const days = daysUntil(next.date);
  const label =
    days === null
      ? ""
      : days <= 0
        ? "On now"
        : days === 1
          ? "Tomorrow"
          : `${days} days to go`;

  return (
    <button
      type="button"
      onClick={() => navigate("/schedule")}
      className="w-full flex items-center justify-between gap-3 rounded-lg px-4 py-3 mt-3 bg-primary/10 border border-primary/20 hover:bg-primary/15 transition-colors text-left"
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary text-primary-content">
          <HiTrophy className="w-4 h-4" />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-base-content font-heading truncate">
            {next.name}
          </p>
          <p className="text-xs text-base-content/60 font-grotesk">
            {formatDateRange(next.date, next.endDate)}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <span className="text-sm font-bold text-primary font-geist tabular-nums">
          {label}
        </span>
        <HiChevronRight className="w-4 h-4 text-base-content/40" />
      </div>
    </button>
  );
};

export default NextEventChip;
